import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Clock, Save } from 'lucide-react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const WorkSchedule = () => {
  const [schedule, setSchedule] = useState({ working_days: [1, 2, 3, 4, 5], half_days: [], shift_start: '08:00', shift_end: '15:30' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSchedule(); }, []);

  const fetchSchedule = async () => {
    try {
      const res = await api.get('/work-schedule');
      setSchedule(res.data.data);
    } catch {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day) => {
    setSchedule(prev => {
      const wd = prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day].sort();
      const hd = prev.half_days.filter(d => wd.includes(d));
      return { ...prev, working_days: wd, half_days: hd };
    });
  };

  const toggleHalf = (day) => {
    if (!schedule.working_days.includes(day)) return;
    setSchedule(prev => ({
      ...prev,
      half_days: prev.half_days.includes(day)
        ? prev.half_days.filter(d => d !== day)
        : [...prev.half_days, day].sort()
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/work-schedule', schedule);
      toast.success('Work schedule updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="schedule-loading"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="work-schedule-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md shadow-indigo-500/20">
            <Clock className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="schedule-title">Work Schedule</h1>
            <p className="text-sm text-slate-500">Configure weekly working days and shift timings</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-500 text-white rounded-lg px-5" data-testid="save-schedule-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
      </div>

      {/* Weekly Schedule */}
      <div className="glass-card p-6">
        <h2 className="text-sm font-bold text-slate-700 mb-4">Weekly Working Days</h2>
        <div className="grid grid-cols-7 gap-3" data-testid="days-grid">
          {DAYS.map((day, i) => {
            const isWorking = schedule.working_days.includes(i);
            const isHalf = schedule.half_days.includes(i);
            return (
              <div key={i} className="text-center" data-testid={`day-${i}`}>
                <button
                  onClick={() => toggleDay(i)}
                  className={`w-full aspect-square rounded-2xl flex flex-col items-center justify-center transition-all duration-200 border-2 ${
                    isWorking
                      ? isHalf
                        ? 'bg-amber-50 border-amber-300 text-amber-700'
                        : 'bg-teal-50 border-teal-300 text-teal-700'
                      : 'bg-slate-50 border-slate-200 text-slate-400'
                  }`}
                  data-testid={`toggle-day-${i}`}
                >
                  <span className="text-lg font-bold">{DAY_ABBR[i]}</span>
                  <span className="text-[10px] font-medium mt-0.5">
                    {isWorking ? (isHalf ? 'Half Day' : 'Working') : 'Off'}
                  </span>
                </button>
                {isWorking && (
                  <button
                    onClick={() => toggleHalf(i)}
                    className={`mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full transition-colors ${
                      isHalf ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-amber-600'
                    }`}
                    data-testid={`toggle-half-${i}`}
                  >
                    {isHalf ? 'Half Day' : 'Set Half'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Shift Timings */}
      <div className="glass-card p-6">
        <h2 className="text-sm font-bold text-slate-700 mb-4">Shift Timings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-lg">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Shift Start</label>
            <input
              type="time"
              value={schedule.shift_start}
              onChange={(e) => setSchedule(p => ({ ...p, shift_start: e.target.value }))}
              className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm"
              data-testid="shift-start-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Shift End</label>
            <input
              type="time"
              value={schedule.shift_end}
              onChange={(e) => setSchedule(p => ({ ...p, shift_end: e.target.value }))}
              className="w-full h-11 px-4 rounded-lg border border-slate-200 text-sm"
              data-testid="shift-end-input"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="glass-card p-6">
        <h2 className="text-sm font-bold text-slate-700 mb-3">Schedule Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 rounded-xl bg-teal-50">
            <p className="text-2xl font-bold text-teal-700">{schedule.working_days.length}</p>
            <p className="text-xs text-teal-600">Working Days</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-slate-50">
            <p className="text-2xl font-bold text-slate-700">{7 - schedule.working_days.length}</p>
            <p className="text-xs text-slate-500">Week Offs</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-amber-50">
            <p className="text-2xl font-bold text-amber-700">{schedule.half_days.length}</p>
            <p className="text-xs text-amber-600">Half Days</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-indigo-50">
            <p className="text-2xl font-bold text-indigo-700">{schedule.shift_start} - {schedule.shift_end}</p>
            <p className="text-xs text-indigo-500">Shift Time</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkSchedule;
